import { Button } from "design-system/atoms/Button"
import { Checkbox } from "design-system/atoms/Checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Switch } from "design-system/atoms/Switch"
import { Command, CommandGroup, CommandItem, CommandList } from "design-system/molecules/Command"
import { FormControl, FormDescription, FormLabel } from "design-system/organisms/Form"
import { ChevronDown, Info } from "lucide-react"
import { useTranslation } from "react-i18next"
import type { OrganizationCompany } from "schema"

type Props = {
  companies: OrganizationCompany[]
  isManager: boolean
  companyIds: string[]
  onManagerChange: (isManager: boolean) => void
  onCompanyIdsChange: (companyIds: string[]) => void
  label: string
  disabled?: boolean
  description?: string
}

export function EmployeeCompanyAccessSelector({
  companies,
  isManager,
  companyIds,
  onManagerChange,
  onCompanyIdsChange,
  label,
  disabled = false,
  description,
}: Props) {
  const { t } = useTranslation()
  const isMultiCompany = companies.length > 1
  const selectedCompanies = companies.filter((company) => companyIds.includes(company.id))

  const selectedLabel = isManager
    ? t("settings.team.inviteEmployee.manager")
    : selectedCompanies.map((company) => company.name).join(", ")

  function handleManagerToggle(enabled: boolean) {
    onManagerChange(enabled)

    if (enabled) {
      onCompanyIdsChange([])
      return
    }

    const onlyCompanyId = companies[0]?.id
    if (!isMultiCompany && onlyCompanyId) {
      onCompanyIdsChange([onlyCompanyId])
    }
  }

  function toggleCompany(companyId: string) {
    if (isManager || disabled) return

    if (companyIds.includes(companyId)) {
      onCompanyIdsChange(companyIds.filter((id) => id !== companyId))
      return
    }

    onCompanyIdsChange([...companyIds, companyId])
  }

  return (
    <>
      <div className="flex items-center justify-between gap-2">
        <FormLabel>{label}</FormLabel>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">
            {t("settings.team.inviteEmployee.manager")}
          </span>
          <Switch checked={isManager} onCheckedChange={handleManagerToggle} disabled={disabled} />
        </div>
      </div>
      {isMultiCompany && (
        <>
          {description && <FormDescription className="text-xs">{description}</FormDescription>}
          <FormControl>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-between font-normal"
                  disabled={isManager || disabled}
                >
                  <span className="truncate text-left">
                    {selectedLabel || t("settings.team.inviteEmployee.companyAccessPlaceholder")}
                  </span>
                  <ChevronDown className="h-5 w-5 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0 min-w-56" align="start">
                <Command>
                  <CommandList>
                    <CommandGroup>
                      {companies.map((company) => {
                        const checked = companyIds.includes(company.id)
                        return (
                          <CommandItem
                            key={company.id}
                            value={company.id}
                            onSelect={() => toggleCompany(company.id)}
                            className="gap-2"
                          >
                            <Checkbox checked={checked} />
                            <span className="truncate">{company.name}</span>
                          </CommandItem>
                        )
                      })}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </FormControl>
        </>
      )}
      <div className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <div>{t("settings.team.inviteEmployee.managerInfo")}</div>
      </div>
    </>
  )
}
